import React, { FC } from 'react';
import { useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { DropDown } from '../../../../components/dropdown/index';
import { Switch } from '../../../../components/switch/index';
import { useLocale } from '../../../../hooks/index';
import { AntiDilutionBaseCombobox } from '../anti-dilution-base';
import { ChildrenFormProps } from '../modals.types';
import { FormSchema, formSchemaStepThree } from '../modals.validation';

export type StepThereProps = ChildrenFormProps;

export const StepThree: FC<StepThereProps> = ({
  control,
  handleCloseModal,
  prevFormStep,
  nextFormStep,
  setFormData,
  lockMode,
}) => {
  const { messagesLocale } = useLocale();
  const { stepThree } = useWatch<FormSchema>({ control });
  const { success } = formSchemaStepThree.safeParse(stepThree);
  const isValid = success;
  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div
          className={twMerge(
            'flex flex-col rounded-md border border-gray-100 bg-white',
            stepThree?.antiDilutionRights?.enabled && 'bg-gray-50',
          )}
        >
          <div className="flex items-center gap-2 px-4 py-3">
            <Switch
              checked={stepThree?.antiDilutionRights?.enabled}
              onChange={(state) => {
                if (state) return setFormData('stepThree.antiDilutionRights.enabled', state);
                setFormData('stepThree.antiDilutionRights', undefined);
              }}
            />
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="ANTI_DILUTION_RIGHTS" />
            </span>
          </div>
          {stepThree?.antiDilutionRights?.enabled && (
            <div className="flex flex-col gap-3 divide-y-[1px] divide-gray-100 rounded-md bg-white px-4 py-3 shadow-sm">
              <AntiDilutionBaseCombobox
                onSelect={(base) => setFormData('stepThree.antiDilutionRights.base', base)}
                selectedBase={stepThree.antiDilutionRights.base}
              />
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-col divide-y-[1px] divide-gray-100 overflow-hidden rounded-lg bg-gray-50">
          <DropDown
            answer={[
              messagesLocale['ANTI_DILUTIONS_EXPLANATION_1'],
              messagesLocale['ANTI_DILUTIONS_EXPLANATION_2'],
              messagesLocale['ANTI_DILUTIONS_EXPLANATION_3'],
              messagesLocale['ANTI_DILUTIONS_EXPLANATION_4'],
            ]}
            question={<AppFormattedMessage id="WHAT_IS_ANTI_DILUTION" />}
          />
        </div>
      </div>

      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          {!lockMode && (
            <Button
              className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
              onClick={prevFormStep}
              styleType="NONE"
              type="button"
            >
              <AppFormattedMessage id="BACK" />
            </Button>
          )}
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            disabled={!isValid}
            onClick={nextFormStep}
            type="button"
          >
            {lockMode ? (
              <AppFormattedMessage id="UPDATE" />
            ) : stepThree?.antiDilutionRights?.enabled ? (
              <AppFormattedMessage id="NEXT" />
            ) : (
              <AppFormattedMessage id="SKIP" />
            )}
          </Button>
        </div>
      </div>
    </>
  );
};
